import React, { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import StandardButton from '@atlaskit/button/standard-button';
import { DropdownItemGroup as DropMenuItemGroup } from '@atlaskit/dropdown-menu';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ScreenIcon from '@atlaskit/icon/glyph/screen';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import TrashIcon from '@atlaskit/icon/glyph/trash';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { FormattedMessage, injectIntl } from '@atlassian/jira-intl';
import DropMenu, { DropMenuItem } from '@atlassian/jira-portfolio-3-common/src/drop-menu/index.tsx';
import PlanDuplicatingAsync from '@atlassian/jira-portfolio-3-plan-duplicating/src/async';
import { DUPLICATE_ERROR_FLAG_ID } from '@atlassian/jira-portfolio-3-plan-duplicating/src/common/constants';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { preload as preloadSettingsPage } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/settings/async';
import docLinks from '@atlassian/jira-portfolio-3-portfolio/src/common/view/doc-link/urls';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { redirectToPlan } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

function PlanSettings(props: Props) {
	const {
		planTitle,
		planId,
		toggleOpenDeleteDialog,
		onDeletePlan,
		intl,
		deleteDialogOpen,
		deleteButtonEnabled,
		isReadOnly,
		defaultOpen,
		redirectToSettings,
		showOnboarding,
		history,
	} = props;

	const [isPlanDuplicatingModalOpen, setIsPlanDuplicatingModalOpen] = useState<boolean>(false);
	const { showFlag } = useFlagsService();

	const goToSettings = () => {
		const redirectFrom = window.location.hash.slice(1);
		redirectToSettings(`/${redirectFrom.split('/')[1]}`);
	};

	const openPlanDuplicatingModal = () => {
		setIsPlanDuplicatingModalOpen(true);
	};

	const closePlanDuplicatingModal = () => {
		setIsPlanDuplicatingModalOpen(false);
	};

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const onPlanDuplicatingComplete = (planId: number, scenarioId: number) => {
		setIsPlanDuplicatingModalOpen(false);
		redirectToPlan(planId, scenarioId);
	};

	const onPlanDuplicatingError = () => {
		setIsPlanDuplicatingModalOpen(false);

		showFlag({
			id: DUPLICATE_ERROR_FLAG_ID,
			type: 'error',
			title: messages.planDuplicatedFlagError,
		});
	};

	const nonReadOnlyMenuItemsBefore = (
		<>
			<DropMenuItem
				elemBefore={<SettingsIcon label={intl.formatMessage(messages.configure)} size="small" />}
				onClick={goToSettings}
			>
				<FormattedMessage {...messages.configure} />
			</DropMenuItem>
			<DropMenuItem
				elemBefore={<CopyIcon label={intl.formatMessage(messages.duplicate)} size="small" />}
				isDisabled={isReadOnly}
				onClick={openPlanDuplicatingModal}
			>
				<FormattedMessage {...messages.duplicate} />
			</DropMenuItem>
			{/* Makes most sense the divider stays here */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles.planSettingsDivider} />
		</>
	);

	const nonReadOnlyMenuItemsAfter = (
		<>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles.planSettingsDivider} />
			<DropMenuItem
				elemBefore={<TrashIcon label={intl.formatMessage(messages.delete)} size="small" />}
				isDisabled={isReadOnly}
				onClick={() => toggleOpenDeleteDialog(true)}
			>
				<FormattedMessage {...messages.delete} />
			</DropMenuItem>
		</>
	);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.planSettings} onMouseEnter={() => preloadSettingsPage()}>
			<Tooltip content={intl.formatMessage(messages.planSettings)}>
				<DropMenu
					defaultOpen={defaultOpen}
					testId="portfolio-3-portfolio.app-simple-plans.top.title-bar.plan-settings"
					// eslint-disable-next-line @typescript-eslint/no-shadow
					trigger={({ triggerRef, ...props }) => (
						<StandardButton
							{...props}
							ref={triggerRef}
							appearance="subtle"
							iconAfter={<MoreIcon label="" />}
							aria-label={intl.formatMessage(messages.planSettings)}
						/>
					)}
				>
					<DropMenuItemGroup>
						{nonReadOnlyMenuItemsBefore}
						<DropMenuItem
							elemBefore={<ScreenIcon label={intl.formatMessage(messages.takeTour)} size="small" />}
							onClick={() => {
								history && history.push('/backlog');
								// the toolbar must be expanded before starting the onboarding tour
								if (!props.showToolBar) {
									props.toggleToolBar();
								}
								showOnboarding();
							}}
						>
							<FormattedMessage {...messages.takeTour} />
						</DropMenuItem>
						<DropMenuItem
							elemBefore={<ShortcutIcon label="" size="small" />}
							href={docLinks.home}
							onClick={(_: MouseEvent | KeyboardEvent, analyticsEvent: UIAnalyticsEvent) => {
								const [actionSubject, action] =
									PRODUCT_ANALYTICS_EVENT_NAMES.HEADER_HELP_LINK_CLICKED.split(' ');
								fireUIAnalytics(analyticsEvent.update({ action, actionSubject }));
							}}
							target="_blank"
						>
							<FormattedMessage {...messages.helpLink} />
						</DropMenuItem>
						{!isReadOnly && nonReadOnlyMenuItemsAfter}
					</DropMenuItemGroup>
				</DropMenu>
			</Tooltip>
			{planId && isPlanDuplicatingModalOpen && (
				<PlanDuplicatingAsync
					planId={planId}
					planTitle={planTitle || ''}
					onClose={closePlanDuplicatingModal}
					onComplete={onPlanDuplicatingComplete}
					onError={onPlanDuplicatingError}
				/>
			)}
			{deleteDialogOpen && (
				<ShortcutScope>
					<ModalDialog autoFocus onClose={() => toggleOpenDeleteDialog(false)}>
						<ModalHeader>
							<ModalTitle appearance="danger">
								{intl.formatMessage(messages.deletePlanHeading)}
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<FormattedMessage
								{...messages.deletePlanDescription}
								values={{
									planTitle,
									strong: (chunks: ReactNode[]) => {
										if (!fg('replace_typography_css_with_tokens_arj')) {
											// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
											return <strong>{chunks}</strong>;
										}
										return <Text as="strong">{chunks}</Text>;
									},
								}}
							/>
						</ModalBody>
						<ModalFooter>
							<StandardButton
								onClick={() => toggleOpenDeleteDialog(false)}
								isDisabled={false}
								appearance="subtle"
							>
								{intl.formatMessage(commonMessages.cancel)}
							</StandardButton>
							<StandardButton
								onClick={() => onDeletePlan()}
								isDisabled={!deleteButtonEnabled}
								appearance="danger"
							>
								{intl.formatMessage(messages.delete)}
							</StandardButton>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			)}
		</div>
	);
}

export default injectIntl(PlanSettings);
